import { NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnChanges,
  SecurityContext,
  signal,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import {
  getActiveBinding,
  getBackground,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
} from '@blaze-shared/elements-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { regexpMatch, urlRegex } from '@blaze-shared/shared-util';

import { arePropsWithData } from '../../canvas-elements/element-model/element-props-guards';
import { ElementHeaderComponent } from '../../components/element-header/element-header.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

@Component({
  selector: 'be-iframe',
  imports: [NgStyle, ElementHeaderComponent],
  templateUrl: './iframe.component.html',
  styleUrls: ['./iframe.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IFrameComponent extends CanvasElementBaseComponent<'iframe'> implements OnChanges {
  private readonly ds = inject(DomSanitizer);

  @Input() value?: string;

  icon!: string;
  iframeSrc = signal<SafeResourceUrl>(this.ds.bypassSecurityTrustResourceUrl(''));
  isValidUrl = true;
  label!: string;
  labelEnabled!: boolean;
  wrapperStyles: Record<string, string | undefined> | null = null;
  iframeBorderStyles!: Record<string, string>;
  iframeStyles!: Record<string, string>;

  get isUrlBound(): boolean {
    return !!getActiveBinding(this.props?.data, 'value');
  }

  get hasUrl(): boolean {
    return !!this.props?.content?.iframe?.url || !!this.value;
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['value']) {
      this.updateIframeUrl(changes['value'].currentValue);
    }

    if (changes?.['props']) {
      const { content } = this.props || {};
      const { title } = content || {};

      this.updateIframeUrl(this.isUrlBound ? this.value : content?.iframe?.url);

      this.label = content?.title?.label || '';
      this.icon = content?.title?.icon || '';
      this.labelEnabled = title?.enabled ?? false;
      this.updateStyles();
    }
  }

  /**
   * Sets iframe src as a safe URL. Depending on how the component is bound to data the passed value
   * can be either a simple URL string or a dataset row.
   * @param value
   */
  private updateIframeUrl(value?: string | { [key: string]: any }): void {
    let url = '';

    const data = arePropsWithData(this.props) ? this.props.data : [];

    if (typeof value === 'object' && this.bindingResults?.['value'] && data?.length) {
      // Handle bound object. This happens when it's bound to an input element
      const binding = getActiveBinding(data, 'value');
      const boundColumnId = binding?.bindings?.value ?? '';
      const boundColumnName = this.getColumnName(boundColumnId) ?? '';
      url = value?.[boundColumnId] ?? value?.[boundColumnName] ?? '';
    } else if (!value || typeof value === 'string') {
      // Handle string value. This happens when iframe is statically typed or when it's bound within a form or details view.
      url = (value || '').trim();
    }

    // Parse as safe url
    if (!url.startsWith('https://') && !url.startsWith('http://')) {
      url = `https://${url}`;
    }
    url = this.ds.sanitize(SecurityContext.URL, url) || '';

    // BL-6640: encode url before verifying to mimic what browser will do with the URL when loading it. This ensure
    // we can properly validate the url against our regexp.
    const isEncodedUrlValid = regexpMatch(urlRegex, encodeURI(url));
    const isRawUrlValid = regexpMatch(urlRegex, url);
    this.isValidUrl = isEncodedUrlValid || isRawUrlValid;
    if (!this.isValidUrl) {
      this.iframeSrc.set(this.ds.bypassSecurityTrustResourceUrl(''));
    } else {
      this.iframeSrc.set(this.ds.bypassSecurityTrustResourceUrl(url));
    }
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { general, label, border, background, titleBlock, container } = style || {};
    if (!style || !general || !label || !border || !background || !titleBlock || !container) {
      return;
    }

    const iframeStyles: Record<string, string> = {};
    const iframeBorderStyles: Record<string, string> = {
      color: label.color.color1,
      ...makeBorderStyles(border.outline, border.width),
    };
    if (content?.title?.iconPosition === 'right') {
      iframeBorderStyles['flex-direction'] = `row-reverse`;
    }
    if (border.radius !== '0px') {
      iframeBorderStyles['border-radius'] = border.radius;
      iframeStyles['border-radius'] = border.radius;
    }
    this.iframeBorderStyles = iframeBorderStyles;

    this.wrapperStyles = {
      ...makeContainerVisualEffectStyles(background.visualEffect),
      ...makeBorderStyles(container.outline, container.outlineWidth),
      padding: general.offset.padding,
      background: getBackground(background.fill),
    };

    this.iframeStyles = iframeStyles;
    safeDetectChanges(this.cdr);
  }
}
